import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Users as UsersIcon,
  Package,
  Truck,
  FileText,
  Receipt,
  BarChart3,
  UserCog,
  LogOut,
  Ear,
  Calendar,
  MessageCircle,
  Bell,
  FilePlus,
  Settings as SettingsIcon,
  Timer as TimerIcon,
  Target,
  ClipboardList,
  PieChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OfflineBanner } from "@/components/OfflineBanner";

const nav = [
  // —— Día a día ——
  { type: "section", label: "Día a día" },
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell, testid: "nav-notifications" },
  { to: "/citas", label: "Citas", icon: Calendar, testid: "nav-appointments" },
  { to: "/pacientes", label: "Pacientes", icon: UsersIcon, testid: "nav-patients" },
  { to: "/chat", label: "Chat interno", icon: MessageCircle, testid: "nav-chat" },

  // —— Caja y facturación ——
  { type: "section", label: "Caja & facturación" },
  { to: "/facturas-rapida", label: "Factura rápida", icon: Receipt, testid: "nav-quick-invoice" },
  { to: "/facturas", label: "Facturas pacientes", icon: FileText, testid: "nav-invoices" },
  { to: "/presupuestos", label: "Presupuestos", icon: FilePlus, testid: "nav-quotes" },

  // —— Stock y productos ——
  { type: "section", label: "Stock & productos", roleHint: "stockAccess" },
  { to: "/stock", label: "Stock", icon: Package, testid: "nav-stock", stockAccess: true },
  { to: "/albaranes", label: "Albaranes", icon: ClipboardList, testid: "nav-deliverynotes", stockAccess: true },

  // —— Análisis ——
  { type: "section", label: "Análisis" },
  { to: "/conversion", label: "Conversión", icon: Target, testid: "nav-conversion" },
  { to: "/estadisticas-pacientes", label: "Estadísticas CRM", icon: PieChart, testid: "nav-patient-stats", adminGerenteOnly: true },
  { to: "/informes", label: "Informes", icon: BarChart3, testid: "nav-reports", adminOnly: true },

  // —— Administración (proveedores/gastos) ——
  { type: "section", label: "Administración", roleHint: "supplierAccess" },
  { to: "/proveedores", label: "Proveedores", icon: Truck, testid: "nav-suppliers", supplierAccess: true },
  { to: "/facturas-proveedores", label: "Gastos", icon: Receipt, testid: "nav-supplier-invoices", supplierAccess: true },
  { to: "/gastos-fijos", label: "Gastos fijos", icon: Receipt, testid: "nav-recurring", adminOnly: true },

  // —— Personal ——
  { type: "section", label: "Personal" },
  { to: "/fichaje", label: "Fichaje", icon: TimerIcon, testid: "nav-timeclock" },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, testid: "nav-users", adminOnly: true },
  { to: "/configuracion", label: "Configuración", icon: SettingsIcon, testid: "nav-settings", globalAdminOnly: true },
];

const ROLES = {
  admin: "Administrador",
  center_admin: "Admin de centro",
  gerente: "Gerente",
  recepcionista: "Recepcionista",
  audioprotesista: "Audioprotesista",
};

function canShowNav(item, role) {
  if (item.type === "section") return true;
  const adminLike = role === "admin" || role === "center_admin";
  if (item.globalAdminOnly && role !== "admin") return false;
  if (item.adminOnly && !adminLike) return false;
  if (item.adminGerenteOnly && !(adminLike || role === "gerente")) return false;
  if (item.supplierAccess && !(adminLike || role === "gerente")) return false;
  if (item.stockAccess && !(adminLike || role === "gerente")) return false;
  return true;
}

// Filtra una sección vacía (sin items visibles para ese rol)
function filterNavWithSections(nav, role) {
  const out = [];
  let lastWasSection = false;
  let lastSection = null;
  for (const item of nav) {
    if (item.type === "section") {
      if (lastWasSection && lastSection) out.pop(); // descarta sección anterior si quedó vacía
      out.push(item);
      lastWasSection = true;
      lastSection = item;
      continue;
    }
    if (canShowNav(item, role)) {
      out.push(item);
      lastWasSection = false;
    }
  }
  // Si la última fue una sección sin items, descártala
  if (lastWasSection && lastSection) out.pop();
  return out;
}

export default function Layout() {
  const { user, logout, centers } = useAuth();
  const navigate = useNavigate();
  const center = centers.find((c) => c.id === user?.center_id);
  const [notifCount, setNotifCount] = useState(0);

  const refreshNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifCount(data.length);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refreshNotifs();
    const t = setInterval(refreshNotifs, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex h-screen bg-[#F7F6F3] overflow-hidden">
      <OfflineBanner />
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E5E3DB] bg-white">
        <div className="px-4 py-4 border-b border-[#E5E3DB] flex justify-center">
          <img
            src="/img/audiocenter-logo.png"
            alt="GestiónAudio"
            className="h-12 w-auto object-contain"
            data-testid="app-logo"
          />
          <span className="sr-only" data-testid="app-name">GestiónAudio</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 scrollbar-thin overflow-y-auto">
          {filterNavWithSections(nav, user?.role).map((n, idx) => {
            if (n.type === "section") {
              return (
                <div
                  key={`section-${idx}-${n.label}`}
                  className={`px-3 pb-1 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#9CA3AF] ${idx === 0 ? "pt-0" : "pt-4"}`}
                >
                  {n.label}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    isActive
                      ? "bg-[#EAE6DF] text-[#2A2F2C] font-medium"
                      : "text-[#4B5563] hover:bg-[#F1EEE8]"
                  }`
                }
              >
                <n.icon strokeWidth={1.5} className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
                {n.to === "/notificaciones" && notifCount > 0 && (
                  <Badge className="bg-[#C07F67] text-white text-[10px] px-1.5 py-0">
                    {notifCount}
                  </Badge>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#E5E3DB] p-4">
          <div className="text-sm font-medium text-[#2A2F2C]" data-testid="user-name">
            {user?.name}
          </div>
          <div className="text-xs text-[#6E7772] mt-0.5">
            {ROLES[user?.role] || user?.role}
            {center && ` · ${center.name}`}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start mt-3 text-[#4B5563] hover:bg-[#F1EEE8]"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid="logout-btn"
          >
            <LogOut strokeWidth={1.5} className="h-4 w-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Mobile bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#E5E3DB] px-4 py-2 flex items-center justify-between">
        <img
          src="/img/audiocenter-logo.png"
          alt="GestiónAudio"
          className="h-14 w-auto object-contain"
        />
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/notificaciones")}
            data-testid="notif-bell-mobile"
            className="relative"
          >
            <Bell className="h-4 w-4" />
            {notifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#C07F67] text-white text-[10px] flex items-center justify-center">
                {notifCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            data-testid="logout-btn-mobile"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="flex-1 md:ml-0 pt-14 md:pt-0 overflow-y-auto h-screen">
        <div className="md:hidden flex overflow-x-auto gap-1 px-2 py-2 border-b border-[#E5E3DB] bg-white scrollbar-thin">
          {nav.filter((n) => n.type !== "section" && canShowNav(n, user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex-shrink-0 text-xs px-3 py-1.5 rounded-full transition-colors ${
                  isActive ? "bg-[#4A5D4E] text-white" : "bg-[#F1EEE8] text-[#4B5563]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="p-6 md:p-8 fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
